import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, Subject } from "rxjs";
import { catchError } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { UserService } from "@app/authentication/user.service";
import { clipDetail } from "./tracking-detail.util";
// From tracking.models rather than the two services, which import THIS file - going the other way
// would make the cycle real the moment anything here needed a value from them.
import { InteractionEventRequest, NavigationEventRequest, TrackedEvent } from "./tracking.models";

interface TrackEventsRequest {
  interactions: InteractionEventRequest[];
  navigations: NavigationEventRequest[];
}

/**
 * The single outbound path for tracking (FLIP-213). Buffers everything fired within one tick of
 * the event loop and posts it as ONE request to api/Tracking/Track.
 *
 * The case this exists for is a user action that produces both kinds of event. Submitting a score
 * fires ScoreSubmitted (interaction) and ScoreSessionCompleted (navigation) back to back in the
 * same callback — previously two HTTP requests and, because each endpoint re-derived the caller's
 * ids, six database round-trips. Now it is one of each.
 *
 * InteractionTrackingService and NavigationTrackingService are thin delegates onto this, so none
 * of the ~30 existing call sites changed.
 */
@Injectable({
  providedIn: "root",
})
export class TrackingDispatchService {
  private interactions: InteractionEventRequest[] = [];
  private navigations: NavigationEventRequest[] = [];
  private flushHandle: number | null = null;
  private eventsSubject = new Subject<TrackedEvent>();

  /**
   * Live stream of every tracked event, emitted synchronously at the call site (FLIP-298) — ahead
   * of, and independent from, the deferred batch POST below. Lets in-app code (e.g. onboarding
   * triggers) react to an existing tracking call for free, with no round trip and no dependence on
   * the API being reachable.
   */
  readonly events$: Observable<TrackedEvent> = this.eventsSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private userService: UserService
  ) {
    if (typeof window !== "undefined") {
      // A buffered batch would otherwise die with the page. Both events are needed: pagehide
      // covers navigation away and bfcache, visibilitychange covers a backgrounded mobile tab
      // that may never fire pagehide at all.
      window.addEventListener("pagehide", () => this.flushBeforeUnload());
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flushBeforeUnload();
        }
      });
    }
  }

  queueInteraction(event: InteractionEventRequest): void {
    this.interactions.push({
      ...event,
      // Clipped here rather than at the ~5 call sites that build a joined list, so every producer
      // is covered including ones added later. previousDetail carries the same markers and the
      // same overflow risk, so it goes through the same clipper.
      detail: clipDetail(event.detail),
      previousDetail: clipDetail(event.previousDetail),
    });
    this.emit(event.eventName, event.entityType, event.entityId, event.correlationId);
    this.schedule();
  }

  queueNavigation(event: NavigationEventRequest): void {
    this.navigations.push(event);
    this.emit(event.eventName, undefined, undefined, event.correlationId);
    this.schedule();
  }

  /**
   * Emits onto events$ before schedule() queues the batch, so subscribers see the event ahead of
   * the network dispatch. Wrapped so a subscriber that throws can't break dispatch itself — Subject
   * calls every observer synchronously and in order, and an uncaught throw from one would otherwise
   * propagate out of queueInteraction/queueNavigation and skip both the push above and schedule().
   */
  private emit(
    eventName: string,
    entityType: string | undefined,
    entityId: number | undefined,
    correlationId: string | undefined
  ): void {
    try {
      this.eventsSubject.next({ eventName, entityType, entityId, correlationId, timestamp: Date.now() });
    } catch {
      // A subscriber's error must never break tracking dispatch.
    }
  }

  /**
   * A macrotask, not a microtask: two `track()` calls in the same synchronous block are the case
   * being coalesced, and setTimeout(0) reliably lands after the current call stack unwinds without
   * delaying the batch into a later frame.
   */
  private schedule(): void {
    if (this.flushHandle !== null) {
      return;
    }

    this.flushHandle = window.setTimeout(() => {
      this.flushHandle = null;
      this.flush();
    }, 0);
  }

  private takeBatch(): TrackEventsRequest | null {
    if (this.interactions.length === 0 && this.navigations.length === 0) {
      return null;
    }

    const batch: TrackEventsRequest = {
      interactions: this.interactions,
      navigations: this.navigations,
    };

    this.interactions = [];
    this.navigations = [];

    return batch;
  }

  /** Fire-and-forget: tracking must never block or break the interaction if it fails. */
  private flush(): void {
    const batch = this.takeBatch();
    if (!batch) {
      return;
    }

    this.http
      .post(`${this.apiBaseUrl}/api/Tracking/Track`, batch)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Unload path. Uses fetch with keepalive rather than HttpClient, because an in-flight XHR is
   * cancelled when the document goes away.
   *
   * Not sendBeacon: that cannot set an Authorization header, and these endpoints are JWT-protected.
   * keepalive supports headers, so the token is attached manually here — raw fetch bypasses
   * JwtInterceptor, which is what would normally do it.
   */
  private flushBeforeUnload(): void {
    if (this.flushHandle !== null) {
      window.clearTimeout(this.flushHandle);
      this.flushHandle = null;
    }

    const batch = this.takeBatch();
    if (!batch) {
      return;
    }

    const headers: { [key: string]: string } = { "Content-Type": "application/json" };
    const token = this.userService.getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      fetch(`${this.apiBaseUrl}/api/Tracking/Track`, {
        method: "POST",
        headers,
        body: JSON.stringify(batch),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Losing analytics on unload is acceptable; throwing during teardown is not.
    }
  }
}
